import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASKS, TASK_TEMPLATES, STATUSES, USERS, LEADS, SERVICES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { buildTaskTemplateRelationParams } from '../taskTemplates/taskTemplateApiHelpers';
import { toast } from 'react-toastify';
import {
  buildTaskRelationParams,
  PRIORITY_OPTIONS,
  formatTaskDeadlineForApi,
  formatTaskDeadlineForInput,
  userLabel,
  leadLabel,
  serviceLabel,
} from './taskApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('responsible_id', String(values.responsible_id));
  fd.append('lead_id', String(values.lead_id));
  fd.append('service_id', String(values.service_id));
  if (values.parent_id) {
    fd.append('parent_id', String(values.parent_id));
  }
  fd.append('title', values.title ?? '');
  fd.append('priority', values.priority ?? '');
  if (values.deadline) {
    fd.append('deadline', formatTaskDeadlineForApi(values.deadline));
  }
  fd.append('description', values.description ?? '');
  fd.append('save_as_template', values.save_as_template ? '1' : '0');

  values.assignees.forEach((user, idx) => {
    fd.append(`assignees[${idx}][id]`, String(user.id));
    fd.append(`assignees[${idx}][role]`, 'assignee');
  });

  values.observers.forEach((user, idx) => {
    fd.append(`observers[${idx}][id]`, String(user.id));
    fd.append(`observers[${idx}][role]`, 'observer');
  });

  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const mapUserOption = (user) => ({
  id: user?.id ?? user?.uuid,
  label: userLabel(user),
  raw: user,
});

const mapLeadOption = (lead) => ({
  id: lead?.id,
  label: leadLabel(lead),
  raw: lead,
});

const mapServiceOption = (service) => ({
  id: service?.id,
  label: serviceLabel(service),
  raw: service,
});

const mapTaskOption = (task) => ({
  id: task?.id,
  label: task?.title ?? String(task?.id ?? ''),
  raw: task,
});

const ensureOption = (list, option) => {
  if (!option?.id) return list;
  return list.some((item) => item.id === option.id) ? list : [...list, option];
};

const CreateTask = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [refLoading, setRefLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const templateFetchSeq = useRef(0);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedObservers, setSelectedObservers] = useState([]);

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [deadline, setDeadline] = useState('');
  const [description, setDescription] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  const [record, setRecord] = useState(null);

  const fetchReferenceData = useCallback(async () => {
    setRefLoading(true);
    try {
      const [
        statusRes,
        usersRes,
        leadsRes,
        servicesRes,
        parentsRes,
      ] = await Promise.allSettled([
        (async () => {
          try {
            return await api.get(STATUSES.BY_MODEL('task'));
          } catch {
            try {
              return await api.get(STATUSES.BY_MODEL('Task'));
            } catch {
              return await api.get(STATUSES.LIST, { params: { paginate: 0 } });
            }
          }
        })(),
        api.get(USERS.LIST, { params: { paginate: 0 } }),
        api.get(LEADS.LIST, { params: { paginate: 0 } }),
        api.get(SERVICES.LIST, { params: { paginate: 0 } }),
        api.get(TASKS.LIST, {
          params: { paginate: 0, ...buildTaskRelationParams() },
        }),
      ]);

      if (statusRes.status === 'fulfilled') {
        const arr = extractList(statusRes.value.data);
        setStatusOptions(
          (Array.isArray(arr) ? arr : []).map((s) => ({
            id: s.id,
            label: statusLabel(s),
            raw: s,
          }))
        );
      } else {
        toast.error('Could not load task statuses');
        setStatusOptions([]);
      }

      if (usersRes.status === 'fulfilled') {
        const arr = extractList(usersRes.value.data);
        setUserOptions((Array.isArray(arr) ? arr : []).map(mapUserOption).filter((u) => u.id));
      } else {
        toast.error('Could not load users');
        setUserOptions([]);
      }

      if (leadsRes.status === 'fulfilled') {
        const arr = extractList(leadsRes.value.data);
        setLeadOptions((Array.isArray(arr) ? arr : []).map(mapLeadOption).filter((l) => l.id));
      } else {
        toast.error('Could not load leads');
        setLeadOptions([]);
      }

      if (servicesRes.status === 'fulfilled') {
        const arr = extractList(servicesRes.value.data);
        setServiceOptions((Array.isArray(arr) ? arr : []).map(mapServiceOption).filter((s) => s.id));
      } else {
        toast.error('Could not load services');
        setServiceOptions([]);
      }

      if (parentsRes.status === 'fulfilled') {
        const arr = extractList(parentsRes.value.data);
        setParentOptions(
          (Array.isArray(arr) ? arr : [])
            .filter((task) => task?.id && task.id !== editId)
            .map(mapTaskOption)
        );
      } else {
        toast.warn('Could not load parent tasks');
        setParentOptions([]);
      }
    } finally {
      setRefLoading(false);
    }
  }, [editId]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    if (isEdit) {
      setTemplateOptions([]);
      setSelectedTemplate(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setTemplateLoading(true);
      try {
        const { data } = await api.get(TASK_TEMPLATES.LIST, {
          params: {
            paginate: 0,
            ...buildTaskTemplateRelationParams(),
          },
        });
        const arr = extractList(data);
        const list = Array.isArray(arr) ? arr : [];
        if (!cancelled) {
          setTemplateOptions(
            list
              .filter((t) => t?.id)
              .map((t) => ({
                id: t.id,
                label: t.title?.trim() ? String(t.title) : `Template #${t.id}`,
                raw: t,
              }))
          );
        }
      } catch {
        if (!cancelled) {
          setTemplateOptions([]);
          toast.warn('Could not load task templates');
        }
      } finally {
        if (!cancelled) setTemplateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  const applyTaskTemplateRecord = useCallback((record) => {
    if (!record) return;
    setTitle(record.title ?? '');
    setPriority(String(record.priority || 'medium').toLowerCase() || 'medium');
    setDescription(record.description ?? '');
  }, []);

  const handleTemplateChange = useCallback(
    async (_, option) => {
      setSelectedTemplate(option);
      if (!option?.id) return;
      const seq = ++templateFetchSeq.current;
      setTemplateLoading(true);
      try {
        const { data } = await api.get(TASK_TEMPLATES.BY_ID(option.id), {
          params: buildTaskTemplateRelationParams(),
        });
        if (seq !== templateFetchSeq.current) return;
        const record = data?.data ?? data;
        if (record) applyTaskTemplateRecord(record);
      } catch (err) {
        if (seq === templateFetchSeq.current) {
          toast.error(err.response?.data?.message || 'Failed to load task template');
        }
      } finally {
        if (seq === templateFetchSeq.current) setTemplateLoading(false);
      }
    },
    [applyTaskTemplateRecord]
  );

  useEffect(() => {
    if (isEdit || statusOptions.length === 0 || selectedStatus) return;
    const pending = statusOptions.find((s) => String(s.label || '').toLowerCase() === 'pending');
    if (pending) setSelectedStatus(pending);
  }, [isEdit, statusOptions, selectedStatus]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(TASKS.BY_ID(editId));
        const payload = data?.data ?? data;
        if (!cancelled) setRecord(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task');
        navigate('/tasks');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editId, isEdit, navigate]);

  useEffect(() => {
    if (!isEdit || !record) return;

    setTitle(record.title ?? '');
    setPriority(record.priority ?? 'medium');
    setDeadline(formatTaskDeadlineForInput(record.deadline));
    setDescription(record.description ?? '');
    setSaveAsTemplate(
      record.save_as_template === true ||
        record.save_as_template === 1 ||
        String(record.save_as_template ?? '') === '1'
    );

    const nestedStatus = record.status && typeof record.status === 'object' ? record.status : null;
    const statusId = record.status_id ?? nestedStatus?.id;
    if (statusId) {
      const option = nestedStatus
        ? { id: statusId, label: statusLabel(nestedStatus), raw: nestedStatus }
        : statusOptions.find((o) => o.id === statusId) ?? { id: statusId, label: String(statusId), raw: {} };
      setSelectedStatus(option);
      setStatusOptions((prev) => ensureOption(prev, option));
    }

    const nestedResponsible =
      record.responsible && typeof record.responsible === 'object' ? record.responsible : null;
    const responsibleId = record.responsible_id ?? nestedResponsible?.id;
    if (responsibleId) {
      const option = nestedResponsible
        ? mapUserOption(nestedResponsible)
        : userOptions.find((o) => o.id === responsibleId) ?? {
            id: responsibleId,
            label: String(responsibleId),
            raw: {},
          };
      setSelectedResponsible(option);
      setUserOptions((prev) => ensureOption(prev, option));
    }

    const nestedLead = record.lead && typeof record.lead === 'object' ? record.lead : null;
    const leadId = record.lead_id ?? nestedLead?.id;
    if (leadId) {
      const option = nestedLead
        ? mapLeadOption(nestedLead)
        : leadOptions.find((o) => o.id === leadId) ?? { id: leadId, label: String(leadId), raw: {} };
      setSelectedLead(option);
      setLeadOptions((prev) => ensureOption(prev, option));
    }

    const nestedService = record.service && typeof record.service === 'object' ? record.service : null;
    const serviceId = record.service_id ?? nestedService?.id;
    if (serviceId) {
      const option = nestedService
        ? mapServiceOption(nestedService)
        : serviceOptions.find((o) => o.id === serviceId) ?? {
            id: serviceId,
            label: String(serviceId),
            raw: {},
          };
      setSelectedService(option);
      setServiceOptions((prev) => ensureOption(prev, option));
    }

    const nestedParent = record.parent && typeof record.parent === 'object' ? record.parent : null;
    const parentId = record.parent_id ?? nestedParent?.id;
    if (parentId) {
      const option = nestedParent
        ? mapTaskOption(nestedParent)
        : parentOptions.find((o) => o.id === parentId) ?? { id: parentId, label: String(parentId), raw: {} };
      setSelectedParent(option);
      setParentOptions((prev) => ensureOption(prev, option));
    } else {
      setSelectedParent(null);
    }

    const assignees = Array.isArray(record.assignees) ? record.assignees : [];
    const mappedAssignees = assignees.map(mapUserOption).filter((u) => u.id);
    setSelectedAssignees(mappedAssignees);
    if (mappedAssignees.length) {
      setUserOptions((prev) => mappedAssignees.reduce((acc, option) => ensureOption(acc, option), prev));
    }

    const observers = Array.isArray(record.observers) ? record.observers : [];
    const mappedObservers = observers.map(mapUserOption).filter((u) => u.id);
    setSelectedObservers(mappedObservers);
    if (mappedObservers.length) {
      setUserOptions((prev) => mappedObservers.reduce((acc, option) => ensureOption(acc, option), prev));
    }
  }, [isEdit, record, statusOptions, userOptions, leadOptions, serviceOptions, parentOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStatus?.id || !selectedResponsible?.id || !selectedLead?.id || !selectedService?.id) {
      toast.warn('Select status, responsible, lead, and service');
      return;
    }
    if (!title.trim()) {
      toast.warn('Enter a task title');
      return;
    }
    if (!deadline) {
      toast.warn('Select a deadline');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      responsible_id: selectedResponsible.id,
      lead_id: selectedLead.id,
      service_id: selectedService.id,
      parent_id: selectedParent?.id ?? '',
      assignees: selectedAssignees,
      observers: selectedObservers,
      title: title.trim(),
      priority,
      deadline,
      description: description.trim(),
      save_as_template: saveAsTemplate,
    };

    const fd = buildFormData(values);
    const q = new URLSearchParams(buildTaskRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${TASKS.BY_ID(editId)}?${q}`, fd);
        toast.success('Task updated');
      } else {
        await api.post(`${TASKS.LIST}?${q}`, fd);
        toast.success('Task created');
      }
      navigate('/tasks');
    } catch (err) {
      toast.error(err.response?.data?.message || (isEdit ? 'Failed to update task' : 'Failed to create task'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')} sx={{ mb: 2 }}>
          Back to tasks
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task' : 'Create task'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {!isEdit ? (
                <Grid item xs={12}>
                  <Autocomplete
                    options={templateOptions}
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    loading={templateLoading}
                    getOptionLabel={(opt) => (opt?.label != null ? String(opt.label) : '')}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Task template"
                        placeholder="Optional — prefills title, priority, and description"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              ) : null}

              {isEdit ? (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={(_, v) => setSelectedStatus(v)}
                    loading={refLoading}
                    getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => <TextField {...params} label="Status" required fullWidth />}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={userOptions}
                  value={selectedResponsible}
                  onChange={(_, v) => setSelectedResponsible(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => <TextField {...params} label="Responsible" required fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={leadOptions}
                  value={selectedLead}
                  onChange={(_, v) => setSelectedLead(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => <TextField {...params} label="Lead" required fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={serviceOptions}
                  value={selectedService}
                  onChange={(_, v) => setSelectedService(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => <TextField {...params} label="Service" required fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={parentOptions}
                  value={selectedParent}
                  onChange={(_, v) => setSelectedParent(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Parent task" placeholder="Optional" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  fullWidth
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedAssignees}
                  onChange={(_, v) => setSelectedAssignees(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => <TextField {...params} label="Assignees" fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedObservers}
                  onChange={(_, v) => setSelectedObservers(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => <TextField {...params} label="Observers" fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Deadline"
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveAsTemplate}
                      onChange={(e) => setSaveAsTemplate(e.target.checked)}
                    />
                  }
                  label="Save as template"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Create task'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/tasks')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTask;
